import { useEffect, useRef } from 'react';
import type { User } from '@hetivo/hetivo-lib-types';
import type { MasterContextValue } from '&/master';
import { useMasterContext } from '&/master';
import type { UseUserScopesReturn } from './use-user-scopes';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UseMasterAutoInitParams {
  scopeInfo: UseUserScopesReturn;
  user: User | null | undefined;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SESSION_STORAGE_KEY = 'hetivo:master-scope';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readRawPersistedScope(): { siteId: string | null; territoryId: string | null } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const territoryId = parsed['territoryId'];
    const siteId = parsed['siteId'];
    const isTerritoryIdValid = territoryId === null || typeof territoryId === 'string';
    const isSiteIdValid = siteId === null || typeof siteId === 'string';
    if (!isTerritoryIdValid || !isSiteIdValid) return null;
    return {
      siteId: typeof siteId === 'string' ? siteId : null,
      territoryId: typeof territoryId === 'string' ? territoryId : null,
    };
  } catch {
    return null;
  }
}

/**
 * Validates a persisted scope against the user's current scopes/permissions.
 * Returns true when the persisted scope is still valid and should be kept.
 * Returns false when the scope is stale/invalid and auto-init should proceed.
 */
function isPersistedScopeValid(
  persisted: { siteId: string | null; territoryId: string | null },
  scopeInfo: UseUserScopesReturn,
): boolean {
  // If nothing is persisted (both null) → let auto-init run
  if (persisted.territoryId === null && persisted.siteId === null) return false;

  return !hasStaleScope(persisted, scopeInfo);
}

function hasStaleScope(
  persisted: { siteId: string | null; territoryId: string | null },
  { singleSiteId, singleTerritoryId }: UseUserScopesReturn,
): boolean {
  if (hasStaleSiteId(persisted.siteId, singleSiteId)) return true;
  if (hasStaleSiteTerritoryId({ persistedTerritoryId: persisted.territoryId, singleSiteId, singleTerritoryId }))
    return true;
  return hasStateTerritoryOnlyId({
    persistedTerritoryId: persisted.territoryId,
    singleSiteId,
    singleTerritoryId,
  });
}

function hasStaleSiteId(persistedSiteId: string | null, singleSiteId: string | null): boolean {
  return !!singleSiteId && !!persistedSiteId && persistedSiteId !== singleSiteId;
}

interface HasStaleSiteTerritoryIdParams {
  persistedTerritoryId: string | null;
  singleSiteId: string | null;
  singleTerritoryId: string | null;
}

function hasStaleSiteTerritoryId({
  persistedTerritoryId,
  singleSiteId,
  singleTerritoryId,
}: HasStaleSiteTerritoryIdParams): boolean {
  return !!singleSiteId && !!persistedTerritoryId && !!singleTerritoryId && persistedTerritoryId !== singleTerritoryId;
}

interface HasStateTerritoryOnlyIdParams {
  persistedTerritoryId: string | null;
  singleSiteId: string | null;
  singleTerritoryId: string | null;
}

function hasStateTerritoryOnlyId({
  persistedTerritoryId,
  singleSiteId,
  singleTerritoryId,
}: HasStateTerritoryOnlyIdParams): boolean {
  return !!singleTerritoryId && !singleSiteId && !!persistedTerritoryId && persistedTerritoryId !== singleTerritoryId;
}

function runAutoInit(scopeInfo: UseUserScopesReturn, context: MasterContextValue): void {
  const {
    firstTerritoryId,
    hasMultipleSites,
    hasMultipleTerritories,
    isTenantScoped,
    singleSiteId,
    singleTerritoryId,
  } = scopeInfo;
  const { selectSite, selectTerritory } = context;

  // 1. Single-site user → auto-select that site
  if (singleSiteId) {
    if (!singleTerritoryId) return;
    selectSite(singleSiteId, singleTerritoryId);
    return;
  }

  // 2. Single-territory user → auto-select the territory (scope-based, not permission-based).
  // This covers both territory admins AND multi-site operators who are scoped to a single
  // parent territory. The territory scope determines the initial context — permissions only
  // control what settings pages they can access, not the MasterContext initialization.
  if (singleTerritoryId) {
    selectTerritory(singleTerritoryId);
    return;
  }

  // 3. Cross-territory-sites user → auto-select first parent territory.
  // These users have sites spanning multiple parent territories. Defaulting to tenant
  // would require tenant settings access they don't have, so we select their
  // first available parent territory.
  if (firstTerritoryId && hasMultipleSites && !isTenantScoped) {
    selectTerritory(firstTerritoryId);
    return;
  }

  // 4. Tenant/multi-territory admins → no-op (stay at tenant level)
  if (isTenantScoped || hasMultipleTerritories) return;

  // Fallback → do nothing
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Auto-initializes MasterContext on first load (when sessionStorage is empty)
 * or after a persisted scope validation failure.
 *
 * Cascade (scope-based, not permission-based):
 * 1. singleSiteId + singleTerritoryId → selectSite(singleSiteId, singleTerritoryId)
 * 2. singleTerritoryId → selectTerritory(singleTerritoryId)
 * 3. firstTerritoryId + hasMultipleSites + !isTenantScoped → selectTerritory(firstTerritoryId)
 * 4. tenant/multiSpace → no-op (stay at tenant level)
 *
 * Also validates existing persisted scope against current user scopes.
 * Invalid scopes (stale, from another user) are cleared and auto-init runs.
 */
export function useMasterAutoInit({ scopeInfo, user }: UseMasterAutoInitParams): void {
  const masterContext = useMasterContext();
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Wait for scope data to be ready
    if (scopeInfo.isLoading || !user) return;

    // Only run once per mount (or until reset by the dep below)
    if (hasRunRef.current) return;

    hasRunRef.current = true;

    const persisted = readRawPersistedScope();

    // If there is a non-empty persisted scope, validate it
    const hasPersistedScope = persisted !== null && (persisted.territoryId !== null || persisted.siteId !== null);
    if (hasPersistedScope) {
      if (isPersistedScopeValid(persisted, scopeInfo)) {
        // Persisted scope is valid — keep it, do not auto-init
        return;
      }
      // Invalid scope — clear it and proceed to auto-init
      masterContext.clearScope();
    }

    runAutoInit(scopeInfo, masterContext);
  }, [scopeInfo, masterContext, user]);
}
